import {Component, Input, NgZone, OnDestroy, OnInit} from "@angular/core";
import {UserService} from "../../../../user/user.service";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import {FormControl} from "@angular/forms";
import {DialogComponent} from "../../../../../ng2-bootstrap-modal/dialog.component";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {Subscription} from "rxjs";
import {Product, ProductType} from "../../product";
import {Permissions} from "../../../../user/permissions";
import {ProductService} from '../../product.service';
import {SearchByPropertyPipe} from '../../../../shared/pipes/search-by-property.pipe';
import {Website} from '../../website';
import {Utils} from "../../../../shared/utils/utils";
import {SortByPipe} from "../../../../shared/pipes/sort-by.pipe";
import {UserSubscription, UserSubscriptionType} from "../../../../user/user-subscription";
import {DbStoreState} from "../../../../store";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../store';
import {ProductActionService} from "../../product-action.service";
import {ActivityAction, ActivityStatus, IActivity} from '../../../../activities/activity';
import {Config} from "../../../../config/config";
import {MultisiteActionConfirmDialogComponent} from "../../../../shared/components/dialogs/mulitiste-action-confirm-dialog/multisite-action-confirm-dialog.component";
import {TextUtils} from "../../../../shared/utils/text-utils";
import {User} from "../../../../user/user";
import {DbModalService} from "../../../../modules/modal/modal";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import { Observable, forkJoin } from "rxjs";
import {filter, first, switchMap} from "rxjs/operators";

export interface IAvailablePluginsData {
  website: Website;
  userPermissions: Permissions;
  userSubscriptions: UserSubscription[];
  currentSubscription: UserSubscription;
}

@Component({
    selector: 'db-available-plugins',
    templateUrl: 'available-plugins.component.html',
    styleUrls: ['available-plugins.component.scss'],
    standalone: false
})
export class AvailablePluginsComponent extends DialogComponent<null, boolean> implements OnInit, OnDestroy, IAvailablePluginsData {

  public ActivityStatus: any = ActivityStatus;

  @Input() website: Website;
  @Input() userPermissions: Permissions;
  @Input() userSubscriptions: UserSubscription[];
  @Input() currentSubscription: UserSubscription;
  @Input() user: User;

  public loading: boolean;

  private _subscriptions: Subscription[];

  private _productsRequestSubscription: Subscription;

  public title: string;
  public changes: any;

  public selectAll: FormControl;
  public searchTerm: FormControl;

  public activeTab: '10web'|'upload'|'wordpress'|'featured-plugins';
  public availablePlugins: Array<string> = ['form-maker', 'photo-gallery', 'ai-assistant-by-10web', 'ai-assistant-by-10web-seo-pack'];
  public availablePluginIds: Array<number> = [95, 101, 205, 206];
  public plugins: Product[];
  private _pluginChildrenMap: any[];
  public filteredPlugins: any[];

  public uploadActivity: IActivity;
  public companyName: string = 'Dashboard';

  constructor(
    private _ngZone: NgZone,
    private _store: Store<DbStoreState>,
    private _searchByPropertyPipe: SearchByPropertyPipe,
    private _userService: UserService,
    private _dialogService: DialogService,
    private _productService: ProductService,
    private _productActionService: ProductActionService,
    private _sortPipe: SortByPipe,
    private _modalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) {
    super(_dialogService);
  }

  ngOnInit() {
    this._getUserSubscription();
    this._getCompanyInfo();
    if (!!this.website.domain_hash) {
      this.availablePlugins.push('tenweb-speed-optimizer');
      this.availablePluginIds.push(177);
    }
    this.initBulkActions();
    this.initFilters();
    this.setActiveTab('10web');
    this.addEventHandlers();
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter(loaded => loaded),
        switchMap(() =>
          this._store.select(fromStore.getUserSubscription)
        ),
      )
      .subscribe(( userSubscription) => {
        this.currentSubscription = userSubscription;
      });
  }

  private initBulkActions(): void {
    this.selectAll = new FormControl('');
  }

  private initFilters(): void {
    this.searchTerm = new FormControl('');
  }

  public setActiveTab(event): void {
      this.activeTab = event.tabId;

      this.loading = true;
      setTimeout((): void => {
        this.updateContent(true);
      }, 150);
  }

  private updateContent(tabChange: boolean = false): void {
    if (!tabChange && this.activeTab === '10web') {
      return;
    }

    this.plugins = null;
    this.filteredPlugins = null;

    switch (this.activeTab) {
      case '10web':
        this.loading = false;
        this.get10WebPlugins();
        break;

      case 'upload':
        this.loading = false;
        break;

      case 'wordpress':
        this.getWordpressPlugins();
        break;

      case 'featured-plugins':
        this.getFeaturedPlugins();
        break;
    }

  }

  private get10WebPlugins(): void {
    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    this._productsRequestSubscription = this._store.select(fromStore.getTenWebPluginsAndExtensions(this.website.id))
      .subscribe((plugins: Product[]) => {
        plugins.map((plugin, index) => {
          if ( this.plugins && this.plugins[index] && this.plugins[index].selected ) {
            plugin.selected = true;
          }
        });
        this.plugins = plugins.filter(plugin => plugin.hasPaidVersion && !(plugin.currentVersion.free === '0.0.0' && !plugin.parentId && plugin.slug !== Config.optimizerPluginSlug && (this.currentSubscription.type === UserSubscriptionType.ONE_PRODUCT || this.currentSubscription.type === UserSubscriptionType.PREMIUM) ));

        /***** remove this part after old plugins disable *****/
        if ( this.user.planType === UserSubscriptionType.ONE_PRODUCT && !this.website.domain_hash) {
          this.plugins = this.currentSubscription && this.currentSubscription.plan && this.currentSubscription.plan.description && this.currentSubscription.plan.description.description && plugins.filter(plugin => plugin.slug === this.currentSubscription.plan.description.description.premiumPluginKey && plugin.hasPaidVersion && !(plugin.currentVersion.free === '0.0.0' && !plugin.parentId && plugin.slug !== Config.optimizerPluginSlug && (this.currentSubscription.type === UserSubscriptionType.ONE_PRODUCT || this.currentSubscription.type === UserSubscriptionType.PREMIUM) ));
        }
        else if (this.user.planType === UserSubscriptionType.PREMIUM && !this.website.domain_hash) {
          this.plugins = plugins.filter(plugin => plugin.hasPaidVersion && !(plugin.currentVersion.free === '0.0.0' && !plugin.parentId && plugin.slug !== Config.optimizerPluginSlug ));
        }
        else {
          this.plugins = plugins.filter(plugin => (this.availablePlugins.includes(plugin.slug) && plugin.hasPaidVersion && !(plugin.currentVersion.free === '0.0.0' && !plugin.parentId && plugin.slug !== Config.optimizerPluginSlug )) || (this.availablePluginIds.includes(plugin.parentId)));
        }

        if (!this.website.domain_hash) {
          this.plugins = this.plugins.filter(plugin => plugin.slug !== Config.optimizerPluginSlug);
        }

        this._pluginChildrenMap = this.getPluginChildrenMap(this.plugins);
        this.filter10WebPlugins();
      });
  }

  private getPluginChildrenMap(pluginsAndAddons: Product[]): any {
    const pluginChildrenMap: any = {};

    pluginsAndAddons.forEach((product: Product): void => {
      if (product.type === ProductType.PLUGIN && !pluginChildrenMap[product.slug]) {
        pluginChildrenMap[product.productId] = {
          plugin: product,
          children: []
        };
      }
    });

    pluginsAndAddons.forEach((product: Product): void => {
      if (product.type === ProductType.ADD_ON && pluginChildrenMap[product.parentId]) {
        pluginChildrenMap[product.parentId].children.push(product);
      }
    });

    return pluginChildrenMap;
  }

  private filter10WebPlugins(): void {
    const sortedProducts: Product[] = <Product[]>this._sortPipe.transform(this.plugins, 'paidVersionAvailable', 'desc');
    const filteredProducts: Product[] = <Product[]>this._searchByPropertyPipe.transform(sortedProducts, {'title': this.searchTerm.value, 'description': this.searchTerm.value});
    let filteredPluginChildrenMap: any[] = Object.values(this._pluginChildrenMap).reduce((filteredPluginChildrenMap: any, pluginWithChildren: any) => {
      const filteredChildren: Product[] = Utils.intersect(pluginWithChildren.children, filteredProducts);

      if (filteredProducts.indexOf(pluginWithChildren.plugin) !== -1 || filteredChildren.length) {
        if (filteredChildren.length && !!this.searchTerm.value) {
          pluginWithChildren.plugin.childrenToggled = true;
        }

        filteredPluginChildrenMap.push({
          plugin: pluginWithChildren.plugin,
          children: filteredChildren
        });
      }

      return filteredPluginChildrenMap;
    }, []);
    filteredPluginChildrenMap = Utils.sort(filteredPluginChildrenMap, (a, b): number => {
      if (b.plugin.hasPaidVersion && !a.plugin.hasPaidVersion) {
        return 1;
      }
      else if (!b.plugin.hasPaidVersion && a.plugin.hasPaidVersion) {
        return -1;
      }
      else if (b.plugin.popularRank > a.plugin.popularRank) {
        return -1;
      }
      else if (b.plugin.popularRank < a.plugin.popularRank) {
        return 1;
      }

      return 0;
    });

    filteredPluginChildrenMap.forEach((filteredPluginChildrenData: any): void => {
      filteredPluginChildrenData.children = Utils.sort(filteredPluginChildrenData.children, (a, b): number => {
        const aPluginAvailable = (a.hasPaidVersion || (a.availableForHosting && this.website.domain_hash));
        const bPluginAvailable = (b.hasPaidVersion || (b.availableForHosting && this.website.domain_hash));
        if ( (aPluginAvailable && bPluginAvailable) || (!aPluginAvailable && !bPluginAvailable) ) {
          if (b.popularRank > a.popularRank) {
            return 1;
          } else if (b.popularRank < a.popularRank) {
            return -1;
          }
        }
        else if (aPluginAvailable && !bPluginAvailable) {
          return -1;
        }
        else if (!aPluginAvailable && bPluginAvailable) {
          return 1;
        }

        return 0;
      });
    });

    this.filteredPlugins = filteredPluginChildrenMap;
  }

  private getWordpressPlugins() {
    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    this._productsRequestSubscription = this._productService.getWordpressPlugins(this.website.id, this.searchTerm.value)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {

        if (response.success) {
          this.plugins = response.data;
          this.filteredPlugins = this.plugins.map((plugin: Product): any => {
            return {
              plugin: plugin,
              children: []
            };
          });
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this.loading = false;
      });
  }

  private getFeaturedPlugins() {
    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    const featuredPluginsRequests: Observable<DbResponse>[] = Config.featuredPlugins
    .map(pluginName =>
      this._productService.getWordpressPlugins(this.website.id, pluginName, true)
      .pipe(first(response => !!response))
      );

    this._productsRequestSubscription = forkJoin(featuredPluginsRequests)
      .subscribe(responses => {

        if (responses.every(response => response.success)) {
          this.plugins = responses.reduce((plugins, response) => ([...plugins, ...response.data]), []);
          this.filteredPlugins = this.plugins.map((plugin: Product): any => {
            return {
              plugin: plugin,
              children: []
            };
          });
        }
        else {
          responses.forEach(response => !response.success && NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER)));
        }
        this.loading = false;
      });
  }

  private addEventHandlers(): void {

    this._subscriptions = [];

    this._subscriptions.push(this.searchTerm.valueChanges.subscribe((): void => {
      switch (this.activeTab) {
        case '10web':
          this.filter10WebPlugins();
          break;

        case 'wordpress':
          this.getWordpressPlugins();
          break;
      }
    }));

    this._subscriptions.push(
      this._store.select(fromStore.getProductUploadOngoingActivity(this.website.id))
        .subscribe((activity: IActivity) => {
          if (this.uploadActivity && !activity) {
            if (this.uploadActivity.status !== ActivityStatus.FAILED) {
              setTimeout(() => {
                this.uploadActivity = activity;
              }, 2000);
            }
            if (this.uploadActivity.status === ActivityStatus.DONE) {
              setTimeout(() => {
                this.close();
              }, 2000);
            }
          }
          else  {
            this.uploadActivity = activity;
          }
        })
    );
  }

  override ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {

    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    if ( this._subscriptions ) {
      this._subscriptions.forEach((subscription: Subscription): void => {
        subscription.unsubscribe();
      });
    }
    this._subscriptions = null;
  }

  public onItemToggled(item: Product, parent?: Product): void {
    if (item.selected && item.hasAddons) {
      item.childrenToggled = true;
    }

    if (parent && item.selected && !parent.selected) {
      parent.selected = true;
    }

    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    this.selectAll.setValue(!this.getManageablePluginsAndAddons().find((product: Product): boolean => !product.selected));
  }

  private getManageablePluginsAndAddons(): Product[] {
    return this.filteredPlugins.reduce((managableProducts: Product[], pluginWithAddons: any): Product[] => {
      const plugin: Product = pluginWithAddons.plugin;

      managableProducts.push(plugin);
      managableProducts = managableProducts.concat(pluginWithAddons.children);

      return managableProducts;
    }, []);
  }

  public toggleAllItemsSelected(selected: boolean): void {
    this.getManageablePluginsAndAddons().forEach((product: Product): void => {
      if (!((product.type === ProductType.ADD_ON || product.slug === Config.builderPluginSlug) && !product.paidVersionAvailable && !(product.availableForHosting && this.website.domain_hash))) {
        product.selected = selected;
      }
    });
  }

  public bulkInstallAndActivate(): void {
    const bulkItems: Product[] = this.getBulkItems();
    if (bulkItems.length) {
      const network = 0;
      if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
        this.openConfirmationDialog(network, ActivityAction.INSTALL_ACTIVATE, bulkItems);
      }
      else {
        this.dispatchActions(network, ActivityAction.INSTALL_ACTIVATE, bulkItems);
      }
    }

    this.toggleAllItemsSelected(false);
    this.updateSelectAllState();
  }

  private getBulkItems(): Product[] {

    return this.filteredPlugins.reduce((bulkItems: Product[], pluginWithAddons: any): Product[] => {
      const plugin: Product = pluginWithAddons.plugin;
      const bulkChildren: Product[] = pluginWithAddons.children.filter((product: Product): boolean => {
        return product.selected && product.canApplyAction(ActivityAction.INSTALL_ACTIVATE);
      });

      if ((plugin.selected || (!plugin.paidVersionInstalled && bulkChildren.length)) && plugin.canApplyAction(ActivityAction.INSTALL_ACTIVATE)) {
        bulkItems.push(plugin);
      }
      bulkItems = bulkItems.concat(bulkChildren);

      return bulkItems;
    }, []);
  }

  public get selectedPluginsAndAddons(): Product[] {
    if (!this.filteredPlugins) {
      return [];
    }

    return this.filteredPlugins.reduce((selectedProducts: Product[], pluginWithAddons: any): Product[] => {
      const plugin: Product = pluginWithAddons.plugin;
      const selectedChildren: Product[] = pluginWithAddons.children.filter((product: Product): boolean => {
        return product.selected;
      });

      if (plugin.selected) {
        selectedProducts.push(plugin);
      }
      selectedProducts = selectedProducts.concat(selectedChildren);

      return selectedProducts;
    }, []);
  }

  public onPluginArchiveSelect($event: any): void {
    this.uploadPlugin($event.target.files[0]);
  }

  public onPluginArchiveDrop($event: any): void {
    const uploadFile: any = $event.files[0];
    if (!uploadFile || !uploadFile.fileEntry) {
      return;
    }

    uploadFile.fileEntry.file((file: File): void => {
      this._ngZone.run(() => {
        this.uploadPlugin(file);
      });
    });
  }

  private uploadPlugin(file: File): void {
    const network = 0;
    if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
      this.openConfirmationDialog(network, ActivityAction.INSTALL_ACTIVATE_UPLOAD, Product.createUploadProduct(file, ProductType.PLUGIN), true);
    }
    else {
      this.dispatchActions(network, ActivityAction.INSTALL_ACTIVATE_UPLOAD, Product.createUploadProduct(file, ProductType.PLUGIN), true);
    }
  }

  public resetUploadState(): void {
    this.uploadActivity = null;
  }

  private openConfirmationDialog(network, action, product, upload = false) {
    this._dialogService
      .addDialog(MultisiteActionConfirmDialogComponent, {
        header: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_multisiteDeactivate.header' : 'text_multisiteActivate.header'),
        message: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_multisiteDeactivate.message' : 'text_multisiteActivate.message'),
        singleSiteButtonText: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_deactivate' : 'text_activate'),
        multisiteButtonText: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_networkDeactivate' : 'text_networkActivate'),
      })
      .subscribe((confirmed: string|boolean) => {
        if (confirmed) {
          if (confirmed === 'network') {
            network = 1;
          }
          this.dispatchActions(network, action, product, upload);
        }
      });
  }

  private dispatchActions(network, action, product, upload = false) {
    if (upload) {
      this._store.dispatch(new fromStore.InitWebsiteProductUploadAction({
        website: this.website,
        products: [product],
        action: action,
        network: network
      }));
    }
    else {
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: this.website,
        products: [product],
        action: action,
        network: network
      }));
    }
  }

  private _getCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfoLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getCompanyInfo)),
        first()
      )
      .subscribe((companyInfo: any) => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
      });
  }

}
